import { isEmpty } from 'lodash'
import { useAppDispatch, useAppSelector } from 'app/state'
import { get } from 'lodash'
import {
  clearDataExplorerTreeLoadingErrors,
  DataExplorerTree,
  fetchDataTreeResourceByLink,
  fetchDataTreeResourceRequest,
  setCurrentSelectedNode,
  setCurrentSelectedTreeItemId,
  setExpandedNodes,
  setCurrentTileset,
  setCurrentTilesetStatus,
  setCurrentTable,
  setCurrentTableError,
  setCurrentTableStatus,
  setCurrentTilesetStats,
  setCurrentTilesetStatsStatus,
  setCurrentTableDataPreview,
  setCurrentTableDataPreviewStatus
} from 'features/data-explorer/state/dataExplorerSlice'
import { setSubscritionsError } from 'features/data-observatory/state/dataObservatorySlice'
import {
  CARTO_DW,
  DataExplorerTreeItem,
  DataTreeItemTypes,
  DataTreeNodeRenderProps,
  DataTreeNodeChildren,
  DataExplorerTable,
  Tileset,
  Raster
} from 'features/data-explorer/types'
import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import RasterIconBase from 'assets/icons/carto-icon-rasters.svg?react'
import TilesetIconBase from 'assets/icons/carto-icon-products-tilesets.svg?react'
import FolderIcon from 'assets/icons/icon-file-folder.svg?react'
import FolderOpenIcon from 'assets/icons/icon-file-folder-open.svg?react'
import TableIconBase from 'assets/icons/icon-editor-table-chart.svg?react'
import { Providers } from 'features/connections/providers'
import {
  myConnectionsResource,
  Connection,
  ProviderType,
  setCurrentConnection,
  getConnectionById
} from 'features/connections'
import { RootState } from 'app/state/store'
import { ApiError, CartoCatalogErrorCode, RequestStatus } from 'features/common'
import { shallowEqual } from 'react-redux'
import { useResource } from 'app/state/resource'
import { isApiError } from 'features/common/api/apiError'
import { isEqual } from 'lodash'
import ProviderIcon from 'features/common/ui/ProviderIcon/ProviderIcon'
import { getNodePath } from 'features/data-explorer/utils'
import { showViewerCredentialsDialog } from 'features/connections/ui/ViewerCredentials'
import { styled } from '@mui/material'

const RasterIcon = styled(RasterIconBase)(({ theme }) => ({
  fill: theme.palette.text.secondary
}))

const TilesetIcon = styled(TilesetIconBase)(({ theme }) => ({
  fill: theme.palette.text.secondary
}))

const TableIcon = styled(TableIconBase)(({ theme }) => ({
  fill: theme.palette.text.secondary
}))

export function getIcon(node: DataExplorerTreeItem, isExpanded: boolean) {
  if (node.type === DataTreeItemTypes.Connection) {
    if (node.carto_dw) {
      return <ProviderIcon size="small" provider={'carto_dw' as ProviderType} />
    }
    const providerMeta = Providers[String(node.provider_id)]
    if (!providerMeta) {
      throw new Error('Invalid provider type')
    }
    return <ProviderIcon size="small" provider={String(node.provider_id) as ProviderType} />
  } else if (node.type === DataTreeItemTypes.Tileset) {
    return <TilesetIcon />
  } else if (node.type === DataTreeItemTypes.Raster) {
    return <RasterIcon />
  } else if (
    node.type === DataTreeItemTypes.Table ||
    node.type === DataTreeItemTypes.View ||
    node.type === DataTreeItemTypes.Data ||
    node.type === DataTreeItemTypes.Geography
  ) {
    return <TableIcon />
  } else {
    const hasContent = Boolean(node.children)
    return isExpanded && hasContent ? <FolderOpenIcon /> : <FolderIcon />
  }
}

export function createChildrenTargetKey(nodeId: string) {
  return `${nodeId}.children`
}

export function makeChildrenNodes(
  parentNodeId: string,
  children: Record<string, DataExplorerTreeItem> | ApiError | undefined,
  childrenFilter?: (item: DataExplorerTreeItem) => boolean
): DataTreeNodeChildren | undefined {
  if (!children || isEmpty(children) || isApiError(children)) {
    return undefined
  }

  const allKeys = Object.keys(children)
  const selectedKeys = childrenFilter
    ? allKeys.filter((key) => {
        const item = children[key]
        return childrenFilter(item)
      })
    : allKeys
  return selectedKeys.map((key) => `${parentNodeId}.${key}`)
}

export interface NodePropsSelectorAll {
  id: string
  label: string
  connectionName?: string
  connectionProvider: string
  connectionId: string
  connectionPrivacy?: string
  connectionImportsAvailable?: boolean
  fqn: string
  expandable?: boolean
  selectable: boolean
  icon: JSX.Element
  children?: DataTreeNodeChildren
  totalChildren: number
  page?: number
  loading: boolean
  type: string
  path: string
  treeItem: DataExplorerTreeItem
  isEmpty: boolean
}
// TODO: Review with @zbigg passing full state
export function nodePropsSelectorCommon(
  nodeId: string,
  isExpanded: boolean,
  state: RootState,
  childrenFilter?: (item: DataExplorerTreeItem) => boolean
): NodePropsSelectorAll | null {
  const treeItem: DataExplorerTreeItem | undefined = get(
    state.dataExplorer.dataExplorerTree,
    nodeId
  ) as unknown as DataExplorerTreeItem
  const loadingNodes = state.dataExplorer.dataExplorerTreeLoadingNodes

  if (!treeItem) {
    return null
  }

  const childrenTargetKey = createChildrenTargetKey(nodeId)

  const isExpandableByType = !isResource(treeItem)
  const displayedChildren = isExpandableByType
    ? makeChildrenNodes(childrenTargetKey, treeItem.children, childrenFilter)
    : undefined
  const expandableNotYetLoaded = isExpandableByType && treeItem.children === undefined
  const expandableHasChildren = isExpandableByType && displayedChildren && displayedChildren?.length > 0

  const expandable = isExpandableByType && (expandableNotYetLoaded || expandableHasChildren)
  const [connectionProvider, connectionId] = nodeId.split('.')
  const connection = state.connections.myConnections.find((c) => c.id === connectionId)
  const path = getNodePath({
    id: treeItem?.id,
    connectionId: connection?.name,
    type: treeItem?.type
  })

  const initialChildrenCount = Object.keys((treeItem.children as object) || {}).length
  const filteredChildrenCount = displayedChildren?.length || 0

  return {
    id: nodeId,
    label: treeItem.carto_dw ? CARTO_DW : treeItem.name || '',
    connectionName: connection?.name,
    connectionProvider,
    connectionId,
    connectionPrivacy: connection?.privacy,
    connectionImportsAvailable: connection?.importsAvailable,
    fqn: treeItem?.id,
    expandable,
    selectable: isResource(treeItem),
    icon: getIcon(treeItem, isExpanded),
    children: displayedChildren,
    totalChildren: (treeItem.totalChildren || 0) - (initialChildrenCount - filteredChildrenCount),
    page: treeItem.page,
    loading: loadingNodes.includes(childrenTargetKey),
    type: treeItem?.type,
    path,
    treeItem: treeItem,
    isEmpty: treeItem.totalChildren === 0
  }
}
export function nodePropsSelectorAll(nodeId: string, isExpanded: boolean, state: RootState) {
  return nodePropsSelectorCommon(nodeId, isExpanded, state)
}

export function findItemInDataTree(root: DataExplorerTree, nodeId: string): DataExplorerTreeItem | undefined {
  return get(root, nodeId) as unknown as DataExplorerTreeItem
}

export interface SelectedResource {
  connectionName: string
  resource?: string
}

export function resourceToExpandedIds(dataExplorerTree: DataExplorerTree, selectedResource: SelectedResource) {
  const result: string[] = []
  const connection = findConnectionByName(dataExplorerTree, selectedResource.connectionName)
  if (!connection) {
    return null
  }
  const connectionNodeId = `${connection.provider_id}.${connection.id}`
  result.push(connectionNodeId)
  if (selectedResource.resource) {
    const selectedResourceIdSplitted = selectedResource.resource.split('.')
    let parentNodeId = connectionNodeId
    for (const partialId of selectedResourceIdSplitted) {
      const nodeId = `${createChildrenTargetKey(parentNodeId)}.${partialId}`
      result.push(nodeId)
      parentNodeId = nodeId
    }
  }
  return result
}

function findConnectionByName(dataExplorerTree: DataExplorerTree, name: string): DataExplorerTreeItem | undefined {
  for (const providerKey in dataExplorerTree) {
    for (const connectionId in dataExplorerTree[providerKey]) {
      const connection = dataExplorerTree[providerKey][connectionId]
      if (connection.name === name) {
        return connection
      }
    }
  }
  return undefined
}

function toggleElement<T>(element: T, list: T[]) {
  const idx = list.indexOf(element)
  if (idx !== -1) {
    const result = list.slice()
    result.splice(idx, 1)
    return result
  } else {
    return [element, ...list]
  }
}

export interface MutableDataTreeState {
  expandedIds: string[]
  selectedId: string
  selectedTreeItemId: string
  setExpandedIds(v: string[]): void
  setSelectedId(v: string): void
  setSelectedTreeItemId(v: string): void
  onClick(nodeId: string): void
}

export function useLocalDataTreeState(): MutableDataTreeState {
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [selectedId, setSelectedId] = useState<string>('')
  const [selectedTreeItemId, setSelectedTreeItemId] = useState<string>('')
  const onClick = useCallback(
    (nodeId: string) => {
      setExpandedIds(toggleElement(nodeId, expandedIds))
    },
    [expandedIds]
  )

  return {
    expandedIds,
    selectedId,
    selectedTreeItemId,
    onClick,
    setExpandedIds,
    setSelectedId,
    setSelectedTreeItemId
  }
}

export function useGlobalDataExplorerTreeState(): MutableDataTreeState {
  const dispatch = useAppDispatch()
  const {
    expandedNodes: expandedIds,
    currentSelectedNode: selectedId,
    currentSelectedTreeItemId: selectedTreeItemId
  } = useAppSelector(
    (state) => ({
      expandedNodes: state.dataExplorer.expandedNodes,
      currentSelectedNode: state.dataExplorer.currentSelectedNode,
      currentSelectedTreeItemId: state.dataExplorer.currentSelectedTreeItemId
    }),
    shallowEqual
  )

  const setExpandedIds = useCallback(
    (expandeIds: string[]) => {
      dispatch(setExpandedNodes(expandeIds))
    },
    [dispatch]
  )

  const setSelectedId = useCallback(
    (selectedId: string) => {
      dispatch(setCurrentSelectedNode(selectedId))
    },
    [dispatch]
  )

  const setSelectedTreeItemId = useCallback(
    (treeItemId: string) => {
      dispatch(setCurrentSelectedTreeItemId(treeItemId))
    },
    [dispatch]
  )

  const onClick = useCallback(
    (nodeId: string) => {
      setExpandedIds(toggleElement(nodeId, expandedIds))
    },
    [expandedIds, setExpandedIds]
  )

  return {
    expandedIds,
    selectedId,
    selectedTreeItemId,
    onClick,
    setExpandedIds,
    setSelectedId,
    setSelectedTreeItemId
  }
}

export function useDataTreeLoader(expandedIds: string[]) {
  const dispatch = useAppDispatch()
  const { dataExplorerTree, dataExplorerTreeStatus, dataExplorerTreeError, dataExplorerTreeLoadingNodes } =
    useAppSelector(
      (state) => ({
        dataExplorerTree: state.dataExplorer.dataExplorerTree,
        dataExplorerTreeStatus: state.dataExplorer.dataExplorerTreeStatus,
        dataExplorerTreeError: state.dataExplorer.dataExplorerTreeError,
        dataExplorerTreeLoadingNodes: state.dataExplorer.dataExplorerTreeLoadingNodes
      }),
      shallowEqual
    )

  useResource(myConnectionsResource)

  const nodeItemMap = useMemo(() => {
    return expandedIds.reduce((r, nodeId) => {
      r[nodeId] = findItemInDataTree(dataExplorerTree, nodeId)
      return r
    }, {} as Record<string, DataExplorerTreeItem | undefined>)
  }, [expandedIds, dataExplorerTree])

  useEffect(() => {
    const loading = dataExplorerTreeLoadingNodes.length > 0 || dataExplorerTreeStatus === RequestStatus.Pending

    if (loading) {
      return
    }
    for (const [nodeId, treeItem] of Object.entries(nodeItemMap)) {
      const isLoadingNode = dataExplorerTreeLoadingNodes.includes(`${nodeId}.children`)
      if (!treeItem || treeItem.children || isResource(treeItem) || isLoadingNode) {
        continue
      }

      const targetKey = createChildrenTargetKey(nodeId)

      if (treeItem.type === DataTreeItemTypes.Connection && !dataExplorerTreeError) {
        const connection = treeItem as Connection

        if (connection.viewerCredentials && !connection.viewerCredentialsAttached) {
          return
        }

        dispatch(
          fetchDataTreeResourceRequest({
            connectionId: treeItem.id,
            targetKey
          })
        )
        return
      } else if (treeItem._links && !isResource(treeItem)) {
        const connectionId = treeItem._links.resources.split('/')[2]
        dispatch(fetchDataTreeResourceByLink({ link: treeItem._links.resources, targetKey, connectionId }))
        return
      }
    }
  }, [nodeItemMap, dataExplorerTreeStatus, dataExplorerTreeLoadingNodes, dispatch, dataExplorerTreeError])

  const clearNodeError = useCallback(
    (nodeId: string) => {
      dispatch(clearDataExplorerTreeLoadingErrors(createChildrenTargetKey(nodeId)))
    },
    [dispatch]
  )
  return {
    ready: dataExplorerTreeLoadingNodes.length === 0 && dataExplorerTreeStatus === RequestStatus.Fulfilled,
    clearNodeError
  }
}

export function useDataTreePreselectedResource({
  dataExplorerTree,
  selectedResource,
  expandedIds,
  selectedId,
  setExpandedIds,
  setSelectedId,
  setSelectedTreeItemId
}: MutableDataTreeState & { dataExplorerTree: DataExplorerTree; selectedResource?: SelectedResource }) {
  const lastSelectedResoureRef = useRef<SelectedResource>()

  // If we got selectedResource from top, update `expanded` && `selectedId`
  useEffect(() => {
    if (!selectedResource || shallowEqual(selectedResource, lastSelectedResoureRef.current)) {
      return
    }

    const resourceExpandedIds = resourceToExpandedIds(dataExplorerTree, selectedResource)
    if (!resourceExpandedIds) {
      return
    }
    const newExpanded = Array.from(new Set([...resourceExpandedIds, ...expandedIds]).values())
    if (!isEqual(expandedIds, newExpanded)) {
      setExpandedIds(newExpanded)
    }

    const newSelectedId = resourceExpandedIds.length > 0 ? resourceExpandedIds[resourceExpandedIds.length - 1] : ''
    if (newSelectedId !== selectedId) {
      setSelectedId(newSelectedId)
    }

    if (selectedResource.resource) {
      setSelectedTreeItemId(selectedResource.resource)
    }

    lastSelectedResoureRef.current = selectedResource
  }, [
    selectedResource,
    expandedIds,
    selectedId,
    dataExplorerTree,
    setExpandedIds,
    setSelectedId,
    setSelectedTreeItemId
  ])
}

export function useNoticationsForDataExplorerErrors() {
  const dataExplorerTreeError = useAppSelector((state) => state.dataExplorer.dataExplorerTreeError)
  const dispatch = useAppDispatch()
  const errorConnection = useAppSelector((state) =>
    getConnectionById(state, { connection: dataExplorerTreeError?.frontendMetadata?.connectionId || '' })
  )
  const userId = useAppSelector((state) => state.users.me?.user_id)
  const canEdit = errorConnection?.user_id === userId

  useEffect(() => {
    if (dataExplorerTreeError?.error && errorConnection) {
      if (dataExplorerTreeError.code === CartoCatalogErrorCode.RequireViewerCredentials) {
        if (!canEdit) {
          showViewerCredentialsDialog({
            connections: [errorConnection],
            onResponse: async (data) => {
              if (data === 'continue') {
                dispatch(
                  fetchDataTreeResourceRequest({
                    connectionId: errorConnection.id,
                    targetKey: `${errorConnection.provider_id}.${errorConnection.id}.children`
                  })
                )
              }
            }
          })
        }
      }
    }
  }, [errorConnection, dataExplorerTreeError, dispatch, canEdit])
}

export function useLoadDataTreeChildren() {
  const dispatch = useAppDispatch()

  return useMemo(() => {
    return {
      getNextPage: (folder: DataTreeNodeRenderProps) => {
        const link = folder.treeItem?._links?.resources || `/connections/${folder.treeItem?.id}/resources`
        const targetKey = createChildrenTargetKey(folder.id)
        dispatch(
          fetchDataTreeResourceByLink({
            link,
            targetKey,
            page: (folder.page || 1) + 1,
            connectionId: folder.treeItem?.id
          })
        )
      },
      refresh: (folder: DataTreeNodeRenderProps) => {
        const link = folder.treeItem?._links?.resources || `/connections/${folder.treeItem?.id}/resources`
        const targetKey = createChildrenTargetKey(folder.id)
        dispatch(
          fetchDataTreeResourceByLink({ link, targetKey, page: 1, refresh: true, connectionId: folder.treeItem?.id })
        )
      },
      search: (folder: DataTreeNodeRenderProps, search: string, persistSearch?: boolean) => {
        if (folder && search) {
          const link = folder.treeItem?._links?.resources || `/connections/${folder.treeItem?.id}/resources`
          const targetKey = createChildrenTargetKey(folder.id)
          dispatch(
            fetchDataTreeResourceByLink({ link, targetKey, search, persistSearch, connectionId: folder.treeItem?.id })
          )
        }
      },
      refreshTreeItem: (folder: DataExplorerTreeItem) => {
        const link = folder?._links?.resources || `/connections/${folder?.id}/resources`
        let targetKey = createChildrenTargetKey(
          `${folder.provider_id}.${folder.connectionId}.children.${folder.id.split('.').join('.children.')}`
        )
        if (folder.type === DataTreeItemTypes.Connection) {
          targetKey = createChildrenTargetKey(`${folder.provider_id}.${folder.connectionId}`)
        }
        dispatch(
          fetchDataTreeResourceByLink({ link, targetKey, page: 1, refresh: true, connectionId: folder?.connectionId })
        )
      },
      refreshResourceDetail: (resource: DataExplorerTable | Tileset | Raster, connectionId: string) => {
        const link = `/connections/${connectionId}/resources/${resource?.id}`
        const targetKey = `${resource.provider}.${connectionId}.children.${resource.id.split('.').join('.children.')}`
        dispatch(fetchDataTreeResourceByLink({ link, targetKey, page: 1, refresh: true, connectionId }))
      }
    }
  }, [dispatch])
}

export function useResetCurrentResource() {
  const dispatch = useAppDispatch()

  const reset = useCallback(() => {
    dispatch(setCurrentConnection())
    dispatch(setCurrentTileset(null))
    dispatch(setCurrentTilesetStatus(RequestStatus.Idle))
    dispatch(setCurrentTilesetStats(null))
    dispatch(setCurrentTilesetStatsStatus(RequestStatus.Idle))
    dispatch(setCurrentTable(null))
    dispatch(setCurrentTableError(null))
    dispatch(setCurrentTableDataPreview(null))
    dispatch(setCurrentTableDataPreviewStatus(RequestStatus.Idle))
    dispatch(setCurrentTableStatus(RequestStatus.Idle))
    dispatch(setSubscritionsError(null))
  }, [dispatch])

  return { reset }
}

export function getFolderChildrenType(folder?: DataTreeNodeRenderProps): DataTreeItemTypes {
  if (!folder) return DataTreeItemTypes.Table
  if (folder.type === DataTreeItemTypes.Connection) {
    return folder.connectionProvider === ProviderType.BIGQUERY ? DataTreeItemTypes.Project : DataTreeItemTypes.Database
  }
  if (folder.type === DataTreeItemTypes.Database) return DataTreeItemTypes.Schema
  if (folder.type === DataTreeItemTypes.Project) return DataTreeItemTypes.Dataset
  if (folder.type === DataTreeItemTypes.View) return DataTreeItemTypes.View
  return DataTreeItemTypes.Table
}

export function getTreePath(connection?: Connection, nodeId = '') {
  return [`${connection?.provider_id}.${connection?.id}`, ...nodeId.split('.')].join('.children.')
}

export function isResource(item: DataExplorerTreeItem | DataTreeNodeRenderProps | undefined): boolean {
  // Since neither DataExplorerTreeItem nor DataTreeNodeRenderProps are hard-typed (type: DataTreeItemTypes) we've to perform check against types as string
  return item !== undefined && item.type !== undefined && ['table', 'view', 'tileset', 'raster'].includes(item.type)
}

export function getAllVisibleNodeIds(dataExplorerTree: DataExplorerTree, connection: Connection): string[] {
  const collectIds = (nodeId: string, acc: string[]): string[] => {
    const item = findItemInDataTree(dataExplorerTree, nodeId)
    if (!item || item.hidden) return acc
    acc.push(nodeId)
    if (item.children && typeof item.children === 'object') {
      Object.keys(item.children).forEach((childKey) => {
        const childNodeId = `${nodeId}.children.${childKey}`
        acc = collectIds(childNodeId, acc)
      })
    }
    return acc
  }
  return collectIds(`${connection.provider_id}.${connection.id}`, [])
}

export function hasLocation(item: DataTreeNodeRenderProps): boolean {
  return !!item?.treeItem?.location
}
